<!-- eslint-disable vue/custom-event-name-casing -->
<script lang="ts" setup>
import type { PaginationProps } from '@autobid/ui/utils/paginationCva'
import { paginationCva } from '@autobid/ui/utils/paginationCva'

interface Props {
  currentPage: number
  pages: number
  color?: PaginationProps['color']
}

const props = defineProps<Props>()

const emits = defineEmits(['paginate', 'paginate:hover'])

const MAX_PAGES = 5
const SEPARATOR = '...'

const pagesToDisplay = computed(() => {
  let result: Array<number | typeof SEPARATOR> = []

  if (MAX_PAGES >= props.pages) {
    result = Array.from({ length: props.pages })
      .fill(0)
      .map((_el, index) => Number(index) + 1)
  }
  else {
    let from = props.currentPage - Math.floor(MAX_PAGES / 2)
    const maxFrom = props.pages - MAX_PAGES

    if (from <= 0) {
      from = 1
    }
    else if (from >= maxFrom) {
      from = maxFrom
    }

    result = Array.from({ length: MAX_PAGES })
      .fill(0)
      .map((_el, index) => +index + from)
  }

  if (result[0] !== 1) {
    if (Number(result[0]) > 2) {
      result.unshift(SEPARATOR)
    }

    result.unshift(1)
  }

  const lastPage = result[result.length - 1]
  if (lastPage !== props.pages) {
    if (typeof lastPage === 'number' && lastPage < props.pages - 1) {
      result.push(SEPARATOR)
    }

    result.push(props.pages)
  }

  return result
})

const amountOfSeparators = computed(
  () => pagesToDisplay.value.filter(el => el === SEPARATOR).length,
)

type Page = number | typeof SEPARATOR

function checkShouldNotPaginate(page: Page) {
  return page === SEPARATOR || props.currentPage === page || props.pages === 1
}

function handlePaginate(page: Page) {
  if (checkShouldNotPaginate(page))
    return

  emits('paginate', page)
}
function handlePaginateHover(page: Page) {
  if (checkShouldNotPaginate(page))
    return

  emits('paginate:hover', page)
}
</script>

<template>
  <!-- eslint-disable vue/custom-event-name-casing -->
  <div v-if="pages > 0" class="flex items-center justify-center gap-4">
    <button
      :disabled="currentPage === 1"
      :class="
        paginationCva({ type: 'previous', disabled: currentPage === 1, color })
      "
      :aria-label="$t('pagination.prev-page')"
      @click="$emit('paginate', currentPage - 1)"
      @mouseover="$emit('paginate:hover', currentPage - 1)"
    >
      <Icon mode="svg" name="mingcute:left-line" class="z-10 text-2xl" />
    </button>

    <component
      :is="page === SEPARATOR ? 'span' : 'button'"
      v-for="page in pagesToDisplay"
      :key="page"
      class="block w-10 items-center text-center"
      :class="{
        'hidden sm:inline-block':
          amountOfSeparators === 2
          && (page === currentPage - Math.floor(MAX_PAGES / 2)
            || page === currentPage + Math.floor(MAX_PAGES / 2)),
        'font-bold !text-black': currentPage === page,
      }"
      :aria-label="
        checkShouldNotPaginate(page)
          ? undefined
          : $t('pagination.go-to-page', { page })
      "
      @click="handlePaginate(page)"
      @mouseover="handlePaginateHover(page)"
    >
      {{ page }}
    </component>

    <button
      :disabled="currentPage === pages"
      :class="
        paginationCva({ type: 'next', disabled: currentPage === pages, color })
      "
      :aria-label="$t('pagination.next-page')"
      @click="$emit('paginate', currentPage + 1)"
      @mouseover="$emit('paginate:hover', currentPage + 1)"
    >
      <Icon mode="svg" name="mingcute:right-line" class="z-10 text-2xl" />
    </button>
  </div>
</template>
